
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { 
  Briefcase, 
  MapPin, 
  Clock, 
  DollarSign, 
  Heart, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Info,
  ThumbsUp, 
  Share2, 
  BookmarkPlus, 
  Building, 
  Sparkles,
  Filter
} from "lucide-react";

import { JobCard } from "@/components/job-swipe/JobCard";
import { JobDetails } from "@/components/job-swipe/JobDetails";
import { SwipeActions } from "@/components/job-swipe/SwipeActions";
import { NoMoreJobs } from "@/components/job-swipe/NoMoreJobs";
import { LikedJobs } from "@/components/job-swipe/LikedJobs";
import { RecentPosts } from "@/components/job-swipe/RecentPosts";
import { useJobSwipe } from "@/hooks/useJobSwipe";
import { useToast } from "@/hooks/use-toast";

import { jobListings } from "@/data/jobListings";
import { Job } from "@/types/job";

// Filters component
const JobFilters = ({ onApply }: { onApply: (filters: any) => void }) => {
  const [jobType, setJobType] = useState("all");
  const [remote, setRemote] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [salary, setSalary] = useState([0, 200000]);
  
  const availableCategories = [
    "Software Development",
    "Design",
    "Marketing",
    "Writing",
    "Customer Support",
    "Data Science",
    "Project Management"
  ];
  
  const handleCategoryChange = (category: string) => {
    if (categories.includes(category)) {
      setCategories(categories.filter(c => c !== category));
    } else {
      setCategories([...categories, category]);
    }
  };
  
  const handleApply = () => {
    onApply({
      jobType,
      remote,
      categories,
      salary
    });
  };
  
  return (
    <div className="p-4 space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Job Type</h3>
        <div className="space-y-2">
          {["all", "full-time", "part-time", "contract", "internship"].map((type) => (
            <div key={type} className="flex items-center">
              <input
                type="radio"
                id={`type-${type}`}
                name="jobType"
                className="mr-2 h-4 w-4 text-blue-600"
                checked={jobType === type}
                onChange={() => setJobType(type)}
              />
              <label htmlFor={`type-${type}`} className="capitalize">
                {type === "all" ? "All Types" : type}
              </label>
            </div>
          ))}
        </div>
      </div>
      
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-medium">Remote Only</h3>
          <Switch checked={remote} onCheckedChange={setRemote} />
        </div>
      </div>
      
      <div>
        <h3 className="text-lg font-medium mb-2">Categories</h3>
        <div className="space-y-2">
          {availableCategories.map((category) => (
            <div key={category} className="flex items-center">
              <input
                type="checkbox"
                id={`category-${category}`}
                className="mr-2 h-4 w-4 text-blue-600"
                checked={categories.includes(category)}
                onChange={() => handleCategoryChange(category)}
              />
              <label htmlFor={`category-${category}`}>{category}</label>
            </div>
          ))}
        </div>
      </div>
      
      <Button onClick={handleApply} className="w-full">
        Apply Filters
      </Button>
    </div>
  );
};

const JobSwipe = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("browse");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<any>(null);
  
  const {
    jobs,
    currentJobIndex,
    swipedDirection,
    showJobDetails,
    likedJobs,
    isTransitioning,
    hasMoreJobs,
    currentJob,
    handleSwipeLeft,
    handleSwipeRight,
    handleViewDetails,
    handleCloseDetails,
    handleResetJobs,
    getCurrentIndex,
    getRemainingJobs,
    getFilteredJobs
  } = useJobSwipe({ 
    initialJobs: jobListings,
    onJobsExhausted: () => {
      toast({
        title: "No more jobs to show",
        description: "You've gone through all available jobs. Check back later for more opportunities!",
      });
    }
  });
  
  const handleShareJob = (job: Job) => {
    // Mock share functionality
    toast({
      title: "Sharing job",
      description: `The job "${job.title}" link has been copied to clipboard.`,
    });
  };

  const handleToggleSave = (job: Job) => {
    // Mock save functionality
    toast({
      title: "Job saved!",
      description: `"${job.title}" has been saved to your profile.`,
    });
  };

  const handleApplyJob = () => {
    // Mock apply functionality
    toast({
      title: "Application started",
      description: "You've started applying for this job. Complete the process to submit your application.",
      variant: "default",
    });
  };

  const handleApplyFilters = (newFilters: any) => {
    setFilters(newFilters);
    setShowFilters(false);
    
    toast({
      title: "Filters applied",
      description: "Your job feed has been updated based on your filters.",
    });
  };

  const getJobMatchScore = (job: Job) => {
    // Logic to calculate match score - this is a simplified version
    // In a real app, this would use more sophisticated algorithms based on user's skills, experience, etc.
    const categories = ["Software Development", "Design", "Marketing", "Writing", "Data Science"];
    const userPreferredCategories = ["Software Development", "Data Science"];
    
    let score = 75; // Base score
    
    // Boost score if job category matches user preferences
    if (job.category && userPreferredCategories.includes(job.category)) {
      score += 15;
    }
    
    // Boost score if job has certain keywords in title or description
    if (job.title.toLowerCase().includes("senior") || job.title.toLowerCase().includes("lead")) {
      score += 5;
    }
    
    // Apply some randomness (in a real app this would be more strategic)
    score += Math.floor(Math.random() * 8);
    
    // Cap at 100
    return Math.min(score, 100);
  };

  return (
    <>
      <Helmet>
        <title>Job Swipe | Find Your Next Opportunity</title>
        <meta name="description" content="Swipe through job opportunities that match your skills and preferences. Find your next perfect role with our intuitive job swiping interface." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50/50 pt-4 pb-20">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          <div className="mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Job Swipe</h1>
              <p className="text-gray-600 mt-1">Discover opportunities tailored to your profile</p>
            </div>
            
            <div className="flex items-center gap-2 self-end">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center gap-1"
                onClick={() => setShowFilters(true)}
              >
                <Filter className="h-4 w-4" />
                Filters
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="text-blue-600 border-blue-200"
                onClick={handleResetJobs}
              >
                Reset
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Tabs defaultValue="browse" value={activeTab} onValueChange={setActiveTab} className="mb-6">
                <TabsList className="grid grid-cols-2 w-full max-w-xs">
                  <TabsTrigger value="browse">
                    <Briefcase className="h-4 w-4 mr-2" />
                    Browse Jobs
                  </TabsTrigger>
                  <TabsTrigger value="liked">
                    <Heart className="h-4 w-4 mr-2" />
                    Liked Jobs
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              
              <TabsContent value="browse" className="mt-0">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 relative min-h-[600px]">
                  {hasMoreJobs ? (
                    <>
                      <div className="mb-6 flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-500 font-medium">
                            Showing job {currentJobIndex + 1} of {jobs.length}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            <Sparkles className="h-3 w-3 mr-1 text-amber-500" />
                            AI Matched
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="relative max-w-xl mx-auto h-[450px]">
                        <AnimatePresence>
                          {currentJob && (
                            <motion.div
                              key={`job-${currentJobIndex}`}
                              initial={{ opacity: 0, scale: 0.95 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ 
                                opacity: 0, 
                                x: swipedDirection === 'left' ? -200 : swipedDirection === 'right' ? 200 : 0,
                                rotateZ: swipedDirection === 'left' ? -10 : swipedDirection === 'right' ? 10 : 0
                              }}
                              transition={{ duration: 0.25 }}
                              style={{ position: 'absolute', width: '100%' }}
                            >
                              <JobCard 
                                job={currentJob} 
                                direction={swipedDirection}
                                onViewDetails={handleViewDetails}
                                matchScore={getJobMatchScore(currentJob)}
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                        
                        {/* Preview of next card */}
                        {jobs[currentJobIndex + 1] && (
                          <div className="absolute top-4 left-0 right-0 opacity-0">
                            <JobCard 
                              job={jobs[currentJobIndex + 1]} 
                              direction={null}
                              onViewDetails={() => {}}
                              matchScore={getJobMatchScore(jobs[currentJobIndex + 1])}
                            />
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-6">
                        <SwipeActions 
                          onSwipeLeft={handleSwipeLeft} 
                          onSwipeRight={handleSwipeRight}
                          disabled={isTransitioning}
                        />
                      </div>
                    </>
                  ) : (
                    <NoMoreJobs 
                      onReset={handleResetJobs} 
                      likedJobsCount={likedJobs.length}
                    />
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="liked" className="mt-0">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 relative min-h-[600px]">
                  <LikedJobs 
                    likedJobs={likedJobs} 
                    onViewDetails={(job) => {
                      // Mock functionality to view details of a liked job
                      toast({
                        title: "Viewing saved job",
                        description: `You're viewing details for "${job.title}".`,
                      });
                    }}
                  />
                </div>
              </TabsContent>
            </div>
            
            <div className="hidden lg:block">
              <RecentPosts />
            </div>
          </div>
          
          {/* Job Details Modal */}
          {currentJob && (
            <JobDetails 
              job={currentJob}
              open={showJobDetails}
              onClose={handleCloseDetails}
              onApply={handleApplyJob}
              matchScore={getJobMatchScore(currentJob)}
              showMatchScore={true}
              isSaved={likedJobs.some(job => job.id === currentJob.id)}
              onToggleSave={() => handleToggleSave(currentJob)}
              onShare={() => handleShareJob(currentJob)}
              hasStartedApplication={false}
            />
          )}
          
          {/* Filters Dialog */}
          <Dialog open={showFilters} onOpenChange={setShowFilters}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Filter Jobs</DialogTitle>
                <DialogDescription>
                  Customize your job feed based on your preferences.
                </DialogDescription>
              </DialogHeader>
              <JobFilters onApply={handleApplyFilters} />
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </>
  );
};

export default JobSwipe;
